import { Button, Checkbox, Col, Divider, Form, FormProps, Input, Row } from 'antd';
import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

// services
import NotificationService from 'core/services/notification';
import { onGetAllRoles } from 'modules/roles/services/roles';
import { onDeleteAdmin, onGetAdmin, onUpdateAdmin } from 'modules/admins/services/admins';

// models
import { Role } from 'core/models/user';
import { Admin } from 'modules/admins/models/Admin';

// components
import BackButton from 'components/backButton/backButton';
import LoadingComponent from 'components/loadingComponent/loadingComponent';
import ConfirmModalComponent from 'components/confirmModal/confirmModal.component';
import Icon from 'utils/icon';

// styles
import styles from './update.module.scss';
import { useTranslation } from 'react-i18next';
import PhoneInputComponent, { PhoneInputReturn } from 'components/phoneInput/phoneInput';
import { checkPhoneValidation } from 'utils/phone';

type FieldType = {
	name: string;
	email: string;
	phone_number: string;
	phone_country_code: string;
};

function UpdateAdminPage() {
	const navigate = useNavigate();
	const { t } = useTranslation();

	const [adminForm] = Form.useForm();

	const [isLoading, setIsLoading] = useState(true);
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [isDeleting, setIsDeleting] = useState(false);
	const [isTogglingStatus, setIsTogglingStatus] = useState(false);
	const [admin, setAdmin] = useState<Admin>();
	const [roles, setRoles] = useState<Role[]>();
	const [selectedRoles, setSelectedRoles] = useState<number[]>([]);
	const [showConfirmation, setShowConfirmation] = useState(false);
	const { id } = useParams();

	useEffect(() => {
		getData();
	}, []);

	const getData = async () => {
		try {
			setIsLoading(true);

			const adminResponse = await onGetAdmin(id as string);

			adminForm.setFieldsValue({
				name: adminResponse.data.data.name,
				email: adminResponse.data.data.email,
				phone_number: adminResponse.data.data.phone_number,
				phone_country_code: adminResponse.data.data.phone_country_code,
			});

			setAdmin(adminResponse.data.data);
			setSelectedRoles(adminResponse.data.data.roles.map((el) => el.id));

			const rolesResponse = await onGetAllRoles();
			setRoles(rolesResponse.data.data.roles);

			setIsLoading(false);
		} catch (error) {
			NotificationService.error(t('COMMON.OPS'), t('COMMON.ERROR_OCCURRED'));
			navigate('/admin');
		}
	};

	const onTogglingStatus = async () => {
		try {
			setIsTogglingStatus(true);
			const updateAdminPayload = new FormData();
			updateAdminPayload.append('active', admin?.active ? '0' : '1');
			updateAdminPayload.append('_method', 'put');
			const response = await onUpdateAdmin(id as string, updateAdminPayload);
			setAdmin(response.data.data);
			setIsTogglingStatus(false);
			NotificationService.success(t('COMMON.SUCCESS'), t('ADMINS.ADMIN_STATUS_CHANGED'));
		} catch (error) {
			setIsTogglingStatus(false);
			NotificationService.error(t('COMMON.OPS'), t('ADMINS.ADMIN_STATUS_NOT_CHANGED'));
		}
	};

	const onDeleteConfirm = async () => {
		try {
			setIsDeleting(true);
			await onDeleteAdmin(Number(id));
			navigate('/admins');
			NotificationService.success(t('COMMON.SUCCESS'), t('ADMINS.ADMIN_DELETED'));
		} catch (error) {
			setIsDeleting(false);
			NotificationService.error(t('COMMON.OPS'), t('ADMINS.ADMIN_NOT_DELETED'));
		}
	};

	const onRoleChange = (id: number) => {
		const idIndex = selectedRoles.findIndex((el) => el === id);

		if (idIndex >= 0) {
			const updatedRoles = selectedRoles.filter((_, index) => index !== idIndex);
			setSelectedRoles(updatedRoles);
		} else {
			const updatedRoles = [...selectedRoles, id];
			setSelectedRoles(updatedRoles);
		}
	};

	const validatePhone = (_: any, value: any) => {
		const phone = '+' + adminForm.getFieldValue('phone_country_code') + value;
		if (checkPhoneValidation(phone)) {
			return Promise.resolve();
		} else if (phone) return Promise.reject(new Error(t('COMMON.PHONE_INVALID')));
		else return Promise.reject();
	};

	const handlePhoneChange = ({ dialCode, number, isValid }: PhoneInputReturn) => {
		if (isValid) {
			adminForm.setFieldsValue({
				phone_country_code: dialCode,
				phone_number: number,
			});
		} else {
			adminForm.setFields([
				{
					name: 'phone_number',
					errors: [t('COMMON.INVALID_PHONE')],
				},
			]);
		}
	};

	const submit: FormProps<FieldType>['onFinish'] = async (values) => {
		try {
			setIsSubmitting(true);

			const updateAdminPayload = new FormData();
			updateAdminPayload.append('_method', 'put');

			updateAdminPayload.append('name', values.name);
			updateAdminPayload.append('email', values.email);
			updateAdminPayload.append('phone_number', values.phone_number);
			updateAdminPayload.append('phone_country_code', values.phone_country_code);

			selectedRoles.forEach((role, i) => {
				updateAdminPayload.append(`roles[${i}]`, role.toString());
			});

			await onUpdateAdmin(id as string, updateAdminPayload);
			setIsSubmitting(false);
			NotificationService.success(t('COMMON.SUCCESS'), t('ADMINS.ADMIN_UPDATED'));
			navigate('/admins');
		} catch (error) {
			setIsSubmitting(false);
			NotificationService.error(t('COMMON.OPS'), t('ADMINS.ADMIN_NOT_UPDATED'));
		}
	};

	return (
		<>
			<div className="pageHeader">
				<BackButton />
				<h3>{t('ADMINS.EDIT_ADMIN')}</h3>
			</div>

			{isLoading ? (
				<LoadingComponent />
			) : (
				<>
					<div className="sectionContainer">
						<div className="d-flex align-items-center justify-content-between flex-wrap">
							<div className="d-flex align-items-center gap-3">
								<h5 className="mb-0">{t('ADMINS.ADMIN_DETAILS')}</h5>
								<span className={`status ${admin?.active ? 'active' : 'inactive'}`}>
									{admin?.active ? t('COMMON.ACTIVE') : t('COMMON.INACTIVE')}
								</span>
							</div>

							<div className="d-flex gap-2">
								<Button type="link" style={{ padding: '0px' }}>
									<Link to="change-password">
										<Icon name="key" width={38} height={38} />
									</Link>
								</Button>

								<Button
									className={admin?.active ? 'danger' : ''}
									type={!admin?.active ? 'primary' : 'default'}
									onClick={onTogglingStatus}
									loading={isTogglingStatus}>
									{admin?.active ? t('ADMINS.DEACTIVATE') : t('ADMINS.ACTIVATE')}
								</Button>

								<Button type="link" style={{ padding: '0px' }} onClick={() => setShowConfirmation(true)}>
									<Icon name="delete" width={38} height={38} />
								</Button>
							</div>
						</div>

						<div className="mt-3">
							<Form name="adminForm" form={adminForm} onFinish={submit}>
								<Row gutter={[16, 16]}>
									<Col span={24} md={12}>
										<p className="mb-0">{t('ADMINS.FULL_NAME')}</p>
										<Form.Item<FieldType> name="name" rules={[{ required: true, message: t('ADMINS.FULL_NAME_REQUIRED') }]}>
											<Input size="large" />
										</Form.Item>
									</Col>

									<Col span={24} md={12}>
										<p className="mb-0">{t('ADMINS.EMAIL')}</p>
										<Form.Item<FieldType> name="email" rules={[{ required: true, message: t('ADMINS.EMAIL_REQUIRED') }]}>
											<Input size="large" />
										</Form.Item>
									</Col>

									<Col span={24} md={12}>
										<p className="mb-0">{t('COMMON.PHONE_NUMBER')}</p>
										<Form.Item hidden name="phone_country_code">
											<Input />
										</Form.Item>
										<Form.Item<FieldType>
											name="phone_number"
											rules={[{ required: true, message: t('COMMON.PHONE_REQUIRED') }, { validator: validatePhone }]}>
											<PhoneInputComponent
												onChange={handlePhoneChange}
												userDialCode={admin?.phone_country_code}
												userPhoneNumber={admin?.phone_number}
											/>
										</Form.Item>
									</Col>
								</Row>
							</Form>
						</div>

						<Divider />

						<h5 className="mb-3">{t('ADMINS.ADMIN_ROLES')}</h5>
						{roles &&
							roles.map((role, i) => (
								<div className={styles.roleSelection} key={i}>
									<Checkbox
										defaultChecked={admin && admin?.roles.findIndex((el) => el.id === role.id) >= 0}
										onChange={() => onRoleChange(role.id)}>
										{role.name}
									</Checkbox>
								</div>
							))}

						<Button type="primary" style={{ marginTop: 20, display: 'flex' }} htmlType="submit" form="adminForm" loading={isSubmitting}>
							{t('COMMON.UPDATE')}
						</Button>
					</div>
				</>
			)}

			<ConfirmModalComponent
				open={showConfirmation}
				title={t('ADMINS.DELETE_ADMIN')}
				confirmText={t('COMMON.DELETE')}
				description={t('ADMINS.DELETE_MODAL_DESCRIPTION')}
				note={t('ADMINS.DELETE_MODAL_NOTE')}
				onDiscard={() => setShowConfirmation(false)}
				onConfirm={() => onDeleteConfirm()}
				isLoading={isDeleting}
			/>
		</>
	);
}

export default UpdateAdminPage;
